import type { CharacterConfig, SkillKind, SkillRegistry } from '../battle/types';

// Shorthand DSL for forcing battle outcomes in tests / dev demos.
//
// Token grammar (one token per attacker per round, separated by whitespace/comma/slash):
//
//   A           base attack (punch) hits, full damage
//   AK          base attack as KICK (heavier default attack) hits
//   AD / AKD    base attack dodged, no counter (punch / kick)
//   ADC / AKDC  base attack dodged + counter
//   ADCK        dodged + counter, and the COUNTER strike is a kick (trailing
//               K; also ADPCK for the buffed-counter variant)
//   AB / AKB    base attack blocked (defender's first block-skill triggers)
//   AS<n>       attack with attack-skill #n (1-based index in attacker's skillIds), hits
//   AS<n>D      skill-attack dodged
//   AS<n>DC     skill-attack dodged + counter
//   AS<n>B      skill-attack blocked (non-pierce skill)
//   AS<n>P      skill-attack pierces block (full damage even vs block-skill)
//   R           repair: actor's first repair-skill triggers, it self-heals
//               instead of attacking (turn ends - no attack/dodge rolls)
//               NOTE: the actor must be BELOW max HP at that point - the
//               engine ignores a repair proc at full HP and keeps attacking,
//               which desyncs the queue (strict rng will throw)
//   -           skip (no attack this round - e.g. dead, or want a no-op slot)
//
//   New (buffs - P = Power-Up; leading prefix, never confuse with the
//   trailing P = pierce or B = block outcomes - position disambiguates):
//   P<n><atk>   leading P[n] = attacker's n-th BUFF-skill triggers BEFORE the
//               attack (bare P = P1). n indexes buff-skills ONLY, a separate
//               space from AS<n> (attack-skills) - e.g. PA, PAK, P1AS1, PADC,
//               P2A. Assumes a non-consuming buff - the attack still follows.
//   A...DPC     dodge + defender's buff + counter (ADPC): defender dodges,
//               procs its first buff-skill, then the boosted counter fires.
//
// The K (kick) flag only affects default attacks - it's ignored with AS<n>
// (a triggered skill drives damage/visual; the engine skips the variant roll).
//
// IMPORTANT - load-bearing convention: scenario assumes p1 wins initiative on
// ties (init rng = 0) and consumes attacker rolls in [p1, p2] order each round.
// If you need agility-based initiative reversal, the scenario can't predict
// the actual swing order - keep agilities equal or write tests w/o forced rolls.
//
// Skill rolls are only injected if the optional `context` argument is provided
// (with skill registry + character configs). Without context, scenario assumes
// 0 skills on both sides (matches current Phase 1 SKILLS map).

type Outcome =
  | 'hit'
  | 'dodge'
  | 'dodgeCounter'
  | 'dodgeBuffCounter'
  | 'block'
  | 'pierceBlock';

type ParsedToken =
  | { kind: 'skip' }
  | { kind: 'repair' }
  | {
      kind: 'attack';
      skillIndex: number;
      kick: boolean;
      // 0 = no buff; n = attacker's n-th buff-skill triggers (bare P = 1).
      attackerBuffIndex: number;
      outcome: Outcome;
      // Trailing K after a counter outcome (ADCK / ADPCK): the counter strike
      // is a kick instead of a punch.
      counterKick: boolean;
    };

const SEPARATOR = /[\s,/]+/;
// Optional leading P<n> = attacker buff (bare P = P1). The trailing P (pierce)
// / B (block) outcomes never collide - prefix is leading, outcome trailing.
// DPC (dodge+defender-buff+counter) must precede DC/D so the longer match wins.
// Two K positions: right after A = attacker kick; the very last char = the
// counter strike is a kick (only valid after a counter outcome).
const TOKEN_RE = /^(?:P(\d*))?A(K)?(?:S(\d+))?(DPC|DC|B|D|P)?(K)?$/;
const OUTCOME_MAP: Record<string, Outcome> = {
  D: 'dodge',
  DC: 'dodgeCounter',
  DPC: 'dodgeBuffCounter',
  B: 'block',
  P: 'pierceBlock',
};

function tokenize(spec: string): ParsedToken[] {
  const chunks = spec.toUpperCase().split(SEPARATOR).filter(Boolean);
  return chunks.map((chunk) => {
    if (chunk === '-') return { kind: 'skip' };
    // R = repair: actor's first repair-skill triggers, it self-heals instead
    // of attacking (turn ends - no attack/dodge/etc rolls after).
    if (chunk === 'R') return { kind: 'repair' };
    const m = TOKEN_RE.exec(chunk);
    if (!m) {
      throw new Error(
        `Unknown token "${chunk}" - expected [P[<n>]]A[K][S<n>][D|DC|DPC|B|P][K], R, or '-'`,
      );
    }
    // m[1] = leading P group: undefined (no P), '' (bare P = buff #1),
    // or digits (P<n> = buff #n).
    const attackerBuffIndex =
      m[1] === undefined ? 0 : m[1] === '' ? 1 : parseInt(m[1], 10);
    const kick = m[2] === 'K';
    const skillIndex = m[3] ? parseInt(m[3], 10) : 0;
    const outcome: Outcome = m[4] ? (OUTCOME_MAP[m[4]] ?? 'hit') : 'hit';
    const counterKick = m[5] === 'K';
    if (counterKick && outcome !== 'dodgeCounter' && outcome !== 'dodgeBuffCounter') {
      throw new Error(
        `Counter-kick "K" suffix in "${chunk}" requires a counter outcome (e.g. ADCK, ADPCK)`,
      );
    }
    return { kind: 'attack', skillIndex, kick, attackerBuffIndex, outcome, counterKick };
  });
}

type SkillCounts = {
  p1AttackSkills: number;
  p1BlockSkills: number;
  p1RepairSkills: number;
  p1BuffSkills: number;
  p2AttackSkills: number;
  p2BlockSkills: number;
  p2RepairSkills: number;
  p2BuffSkills: number;
};

function countKind(
  skillIds: readonly string[],
  registry: SkillRegistry,
  kind: SkillKind,
): number {
  let n = 0;
  for (const id of skillIds) {
    if (registry.get(id)?.kind === kind) n++;
  }
  return n;
}

function computeSkillCounts(ctx: ScenarioContext | undefined): SkillCounts {
  if (!ctx) {
    return {
      p1AttackSkills: 0,
      p1BlockSkills: 0,
      p1RepairSkills: 0,
      p1BuffSkills: 0,
      p2AttackSkills: 0,
      p2BlockSkills: 0,
      p2RepairSkills: 0,
      p2BuffSkills: 0,
    };
  }
  return {
    p1AttackSkills: countKind(ctx.p1.skillIds, ctx.skills, 'attack'),
    p1BlockSkills: countKind(ctx.p1.skillIds, ctx.skills, 'block'),
    p1RepairSkills: countKind(ctx.p1.skillIds, ctx.skills, 'repair'),
    p1BuffSkills: countKind(ctx.p1.skillIds, ctx.skills, 'buff'),
    p2AttackSkills: countKind(ctx.p2.skillIds, ctx.skills, 'attack'),
    p2BlockSkills: countKind(ctx.p2.skillIds, ctx.skills, 'block'),
    p2RepairSkills: countKind(ctx.p2.skillIds, ctx.skills, 'repair'),
    p2BuffSkills: countKind(ctx.p2.skillIds, ctx.skills, 'buff'),
  };
}

// Pushes the rng values for one attacker's turn. RNG order MUST match
// BattleEngine.ts: repair-skill rolls FIRST, then (if no repair triggered)
// attack-skill rolls, [default-attack variant roll - only when no skill
// triggered], dodge, then either counter OR block-skill rolls + block-counter.
function pushAttackerRolls(
  token: ParsedToken,
  attackerAttackSkillCount: number,
  attackerRepairSkillCount: number,
  attackerBuffSkillCount: number,
  defenderBlockSkillCount: number,
  defenderBuffSkillCount: number,
  queue: number[],
): void {
  if (token.kind === 'skip') return;

  // 0. Repair-skill rolls (engine rolls these before anything else).
  if (token.kind === 'repair') {
    if (attackerRepairSkillCount === 0) {
      throw new Error('R token requires the actor to have at least one repair-skill');
    }
    queue.push(0); // first repair-skill triggers -> turn is a self-heal, done.
    return;
  }
  // Attack token: every repair-skill rolls fail so the engine falls through
  // to normal attack resolution.
  for (let i = 0; i < attackerRepairSkillCount; i++) queue.push(1);

  // 0b. Attacker buff rolls (engine: after repair, before attack-skill). Only
  // when the actor has buff skills. P<n> -> n-th buff triggers (the n-1 before
  // it fail); no P -> all buff skills fail. Index space is buff-skills ONLY,
  // separate from attack-skill S<n>. Assumes non-consuming (attack follows).
  if (attackerBuffSkillCount > 0) {
    if (token.attackerBuffIndex === 0) {
      for (let i = 0; i < attackerBuffSkillCount; i++) queue.push(1);
    } else {
      if (token.attackerBuffIndex > attackerBuffSkillCount) {
        throw new Error(
          `Buff index ${token.attackerBuffIndex} exceeds attacker's buff-skill count (${attackerBuffSkillCount})`,
        );
      }
      for (let i = 0; i < token.attackerBuffIndex - 1; i++) queue.push(1);
      queue.push(0);
    }
  } else if (token.attackerBuffIndex > 0) {
    throw new Error('P<n> requires the actor to have at least one buff-skill');
  }

  // 1. Attack-skill rolls (engine stops at first success).
  if (token.skillIndex === 0) {
    for (let i = 0; i < attackerAttackSkillCount; i++) queue.push(1);
  } else {
    if (token.skillIndex > attackerAttackSkillCount) {
      throw new Error(
        `Skill index ${token.skillIndex} exceeds attacker's attack-skill count (${attackerAttackSkillCount})`,
      );
    }
    for (let i = 0; i < token.skillIndex - 1; i++) queue.push(1);
    queue.push(0);
  }

  // 1b. Default-attack variant roll - engine rolls this ONLY when no
  // attack-skill triggered (token.skillIndex === 0). 0 = kick (< KICK_CHANCE),
  // 1 = punch. Skill tokens skip it (engine does too).
  if (token.skillIndex === 0) {
    queue.push(token.kick ? 0 : 1);
  }

  // 2. Dodge roll
  const dodged =
    token.outcome === 'dodge' ||
    token.outcome === 'dodgeCounter' ||
    token.outcome === 'dodgeBuffCounter';
  queue.push(dodged ? 0 : 1);

  if (dodged) {
    // 2b. Defender buff rolls (engine: after dodge success, before counter).
    // Only when the defender has buff skills. DBC -> first buff triggers.
    if (defenderBuffSkillCount > 0) {
      if (token.outcome === 'dodgeBuffCounter') queue.push(0);
      else for (let i = 0; i < defenderBuffSkillCount; i++) queue.push(1);
    }
    // 3a. Counter roll (only if dodge succeeded)
    const counters =
      token.outcome === 'dodgeCounter' || token.outcome === 'dodgeBuffCounter';
    queue.push(counters ? 0 : 1);
    // 3b. Counter-strike variant - engine rolls it ONLY when the counter
    // fired. 0 = kick (< KICK_CHANCE), 1 = punch. No counter -> no roll.
    if (counters) queue.push(token.counterKick ? 0 : 1);
    return;
  }

  // 3b. Block-skill rolls (skipped if attack pierces or no defender block-skills)
  if (token.outcome === 'pierceBlock') return;

  if (token.outcome === 'block') {
    if (defenderBlockSkillCount === 0) {
      throw new Error('AB outcome requires defender to have at least one block-skill');
    }
    queue.push(0); // first block-skill triggers
    queue.push(1); // block-skill counter disabled (no DSL token for it yet)
  } else {
    // outcome === 'hit' - all defender block-skills must fail.
    for (let i = 0; i < defenderBlockSkillCount; i++) queue.push(1);
  }
}

function buildRngQueue(
  p1Tokens: ParsedToken[],
  p2Tokens: ParsedToken[],
  counts: SkillCounts,
): number[] {
  const rounds = Math.min(p1Tokens.length, p2Tokens.length);
  const queue: number[] = [];
  for (let i = 0; i < rounds; i++) {
    queue.push(0); // initiative tie-break (p1 wins ties)
    pushAttackerRolls(
      p1Tokens[i]!,
      counts.p1AttackSkills,
      counts.p1RepairSkills,
      counts.p1BuffSkills,
      counts.p2BlockSkills,
      counts.p2BuffSkills,
      queue,
    );
    pushAttackerRolls(
      p2Tokens[i]!,
      counts.p2AttackSkills,
      counts.p2RepairSkills,
      counts.p2BuffSkills,
      counts.p1BlockSkills,
      counts.p1BuffSkills,
      queue,
    );
  }
  return queue;
}

export type ScenarioContext = {
  p1: CharacterConfig;
  p2: CharacterConfig;
  skills: SkillRegistry;
};

export type Scenario = {
  rng: () => number;
  rounds: number;
};

export function buildScenario(
  p1Spec: string,
  p2Spec: string,
  context?: ScenarioContext,
): Scenario {
  const p1Tokens = tokenize(p1Spec);
  const p2Tokens = tokenize(p2Spec);
  const counts = computeSkillCounts(context);
  const queue = buildRngQueue(p1Tokens, p2Tokens, counts);
  let i = 0;
  // strict: reading past the queue means the DSL desynced from the engine's
  // rng order (or a token misuse like R at full HP) - fail loudly instead of
  // silently switching to Math.random and flaking
  const rng = () => {
    if (i >= queue.length) {
      throw new Error(
        `Scenario rng queue exhausted after ${queue.length} rolls - DSL is out of sync with the engine roll order`,
      );
    }
    return queue[i++]!;
  };
  return { rng, rounds: Math.min(p1Tokens.length, p2Tokens.length) };
}
